import * as React from 'react';
import { useIntl } from 'react-intl';

import * as Styled from './floating-button.styles';
import type { FloatingButtonProps } from './floating-button.types';

export const FloatingButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  FloatingButtonProps
>(
  (
    {
      className,
      disabled,
      href,
      icon: Icon,
      loading,
      onClick,
      size = 'medium',
      sizeConfined,
      sizeWide,
      to,
      type,
      variant,
      ...props
    },
    forwardedRef
  ) => {
    const intl = useIntl();

    const commonProps = {
      $size: size,
      $sizeConfined: sizeConfined,
      $sizeWide: sizeWide,
      $variant: variant,
      'aria-busy': loading,
      'aria-disabled': disabled || loading,
      'aria-label': loading
        ? intl.formatMessage({
            id: 'XDS_A11Y_LOADING',
            defaultMessage: 'Loading',
          })
        : undefined,
      'data-loading': loading,
      'data-xds': 'FloatingButton',
      className,
      disabled: disabled || loading,
      onClick,
      ref: forwardedRef,
    };

    const buttonContent = (
      <>
        {!loading && Icon && (
          <Styled.IconContainer
            $size={size}
            $sizeConfined={sizeConfined}
            $sizeWide={sizeWide}
          >
            <Icon aria-hidden="true" />
          </Styled.IconContainer>
        )}
        {loading && (
          <Styled.Loading
            $size={size}
            $sizeConfined={sizeConfined}
            $sizeWide={sizeWide}
            aria-hidden="true"
          />
        )}
      </>
    );

    if (to || href) {
      return (
        <Styled.RouterFloatingButton
          {...(to && { to })}
          {...(href && { href })}
          {...commonProps}
          {...props}
        >
          {buttonContent}
        </Styled.RouterFloatingButton>
      );
    }

    return (
      <Styled.FloatingButton type={type} {...commonProps} {...props}>
        {buttonContent}
      </Styled.FloatingButton>
    );
  }
);

FloatingButton.displayName = 'FloatingButton';
